(function () {
  /**
   * Normalizes complex precision strings (BMPE) to a numeric value in microns.
   * Factors in base value and slope (L/xxx) at a 300mm reference length.
   */
  function parsePrecision(str) {
    if (!str || str.trim() === '') return 999;
    
    // Clean string: remove spaces, ±, parentheses
    let clean = str.replace(/[±()\s]/g, '').toLowerCase();
    
    // If multiple values exist (e.g., "2µm / 0.0001"), take the first one
    clean = clean.split('/')[0];

    let base = 0;
    let slope = 0;

    // 1. Extract base microns or inches
    // Match numbers before µm, um, μm, microns OR " , ″, '', inch, in
    const micronMatch = clean.match(/([\d.]+)(µm|um|μm|microns)/);
    const inchMatch = clean.match(/([\d.]+)(["″]|''|inch|in)/);

    if (micronMatch) {
      base = parseFloat(micronMatch[1]);
    } else if (inchMatch) {
      base = parseFloat(inchMatch[1]) * 25400; // Convert inches to microns
    } else {
      // Fallback: if no unit, these values are stored as Millimeters (mm)
      const numMatch = clean.match(/[\d.]+/);
      if (numMatch) {
        const val = parseFloat(numMatch[0]);
        // Heuristic: if < 0.1 it's likely Millimeters (e.g. 0.0012mm), 
        // if >= 0.1 it's likely already Microns or a calculated score (e.g. 3.3µm)
        if (val < 0.1) {
          base = val * 1000;
        } else {
          base = val;
        }
      }
    }

    // 2. Extract slope (L/xxx)
    // Match l/ followed by digits
    const slopeMatch = clean.match(/l\/(\d+)/);
    if (slopeMatch) {
      const divisor = parseFloat(slopeMatch[1]);
      if (divisor !== 0) {
        slope = 300 / divisor; // 300mm reference length
      }
    }

    return base + slope;
  }

  /** Does one active filter value match this card? (shared by filtering and empty-pill pruning) */
  function matchOne(card, key, attrVal, f) {
    let match = false;
    if (f.type === 'lookup') {
      if (attrVal === null || String(attrVal).trim() === '') return match;
      const hiVal = parseFloat(attrVal);
      const lowAttrL = card.getAttribute('data-' + key + '-low');
      const loVal = (lowAttrL !== null && lowAttrL.trim() !== '') ? parseFloat(lowAttrL) : 0;
      const tol = 0.0005; // small tolerance for mm entries and rounded ranges
      if (!Number.isNaN(hiVal) && hiVal > 0 && f.value >= loVal - tol && f.value <= hiVal + tol) {
        match = true;
      }
      return match;
    }

    if (f.type === 'range') {
      // Products with no value for this filter never match a range
      if (attrVal === null || String(attrVal).trim() === '') return match;

      // Span ranges (e.g. a 1-2" micrometer): when the block has a low-end
      // metafield, the card carries data-<id>-low. Match when the product's
      // span overlaps the pill's span, so a 0-3" set shows under 0-1", 1-2" and 2-3".
      const lowAttr = card.getAttribute('data-' + key + '-low');
      if (lowAttr !== null && lowAttr.trim() !== '') {
        const lo = parseFloat(lowAttr);
        const hi = parseFloat(attrVal);
        if (!Number.isNaN(lo) && !Number.isNaN(hi) && hi > 0 && lo < f.max && hi > f.min) {
          match = true;
        }
        return match;
      }

      // Determine if we should use smart precision parsing
      let v;
      let isSmart = f.isSmartPrecision;
      
      // Auto-detect if value looks like a precision string
      if (!isSmart && attrVal && (attrVal.includes('µm') || attrVal.includes('μm') || attrVal.includes('um') || attrVal.includes('L/') || attrVal.includes('"') || attrVal.includes('″') || attrVal.includes('in'))) {
        isSmart = true;
      }

      // Plain decimal values (e.g. "0.001", "0.2") are compared as-is.
      // Smart precision parsing only applies to strings with units or L/ slopes.
      const isPlainNumber = /^\s*-?\d*\.?\d+\s*$/.test(attrVal || '');

      if (isSmart && !isPlainNumber) {
        v = parsePrecision(attrVal);
      } else {
        v = parseFloat(attrVal || 0);
      }

      if (!Number.isNaN(v) && v >= f.min && v <= f.max) {
        match = true;
      }
    }

    if (f.type === 'bool') {
      if (attrVal !== null && String(attrVal) === f.value) {
        match = true;
      }
    }

    if (f.type === 'tag') {
      if (attrVal !== null) {
        const tagVal = String(f.value);

        // ── NUMERIC RANGE mode ──────────────────────────────────────────
        // If the pill value is "min:max" (e.g. "0:6.1"), do a numeric
        // comparison against the product metafield value instead of a
        // string-contains match.  This lets a tag filter target a numeric
        // metafield like custom.high_range_x_in by specifying ranges such
        // as  0:6.1|6" Calipers  or  6.2:8.1|8" Calipers
        // in the Options field.
        const rangePattern = /^([\d.]+):([\d.]+)$/;
        const rangeMatch = tagVal.match(rangePattern);

        if (rangeMatch) {
          const minRange = parseFloat(rangeMatch[1]);
          const maxRange = parseFloat(rangeMatch[2]);

          // Parse the metafield value as a plain number.
          // Strip any trailing inch symbols so parseFloat works cleanly
          // (e.g. "6.1", "6.1\"", "6.1in" all become 6.1).
          const stripped = attrVal.replace(/["""''in\s]+$/i, '').trim();
          const numVal = parseFloat(stripped);

          if (!Number.isNaN(numVal) && numVal >= minRange && numVal <= maxRange) {
            match = true;
          }
        } else {
          // ── Original string-contains match (backward compatible) ────
          const val = String(attrVal).toLowerCase();
          if (val.includes(tagVal.toLowerCase())) {
            match = true;
          }
        }
      }
    }
    return match;
  }

  /**
   * Hides pills that no product on this page could match, and whole filter groups
   * with nothing left to pick. Lets one filter setup serve many collections (e.g. the
   * default collection template) without dead-end pills. Only runs once every product
   * is loaded, so a pill isn't hidden just because its products are on a later page.
   */
  function pruneEmptyPills(wrapper) {
    if (nextBatchSize(wrapper) > 0) return;
    const cards = Array.from(wrapper.querySelectorAll('.product-card'));
    wrapper.querySelectorAll('.filter-group').forEach((group) => {
      const key = group.dataset.filter;
      if (!key) return;

      // Size lookup: hide when no product carries a size for it
      if (group.querySelector('.cf-lookup')) {
        const any = cards.some((c) => {
          const v = parseFloat(c.getAttribute('data-' + key));
          return !Number.isNaN(v) && v > 0;
        });
        group.style.display = any ? '' : 'none';
        return;
      }
      if (group.querySelector('.range-slider-wrap')) return;

      const isSmart = key.includes('accuracy') || key.includes('precision') || key.includes('bmpe');
      const buttons = Array.from(group.querySelectorAll('.pill-wrap button[data-type]'));
      let shown = 0;
      buttons.forEach((btn) => {
        const f = {
          type: btn.dataset.type,
          min: parseFloat(btn.dataset.min || 0),
          max: parseFloat(btn.dataset.max || 999999),
          value: btn.dataset.value,
          isSmartPrecision: isSmart
        };
        const has = btn.classList.contains('active') ||
          cards.some((c) => matchOne(c, key, c.getAttribute('data-' + key), f));
        btn.style.display = has ? '' : 'none';
        if (has) shown++;
      });
      if (buttons.length) group.style.display = shown ? '' : 'none';
    });
  }

  function applyFilters(wrapper, reset) {
    const cards = wrapper.querySelectorAll('.product-card');
    const groups = wrapper.querySelectorAll('.filter-group');
    const active = {};

    groups.forEach((group) => {
      const name = group.dataset.filter;
      const badge = group.querySelector('.filter-count-badge');
      let groupActiveCount = 0;
      
      // Handle Slider Filters
      const slider = group.querySelector('.range-slider-wrap');
      const lookup = group.querySelector('.cf-lookup');
      if (lookup) {
        // Size lookup: shopper types a size; products whose low-high span contains it match
        const input = lookup.querySelector('.cf-lookup-input');
        const unitSel = lookup.querySelector('.cf-lookup-unit');
        let v = input ? parseFloat(input.value) : NaN;
        if (!Number.isNaN(v) && v > 0) {
          if (unitSel && unitSel.value === 'mm') v = v / 25.4;
          groupActiveCount = 1;
          active[name] = [{ type: 'lookup', value: v }];
        }
      } else if (slider) {
        const minInput = slider.querySelector('.min-range');
        const maxInput = slider.querySelector('.max-range');
        const minVal = parseFloat(minInput.value);
        const maxVal = parseFloat(maxInput.value);
        const minLimit = parseFloat(slider.dataset.min);
        const maxLimit = parseFloat(slider.dataset.max);

        if (minVal > minLimit || maxVal < maxLimit) {
          groupActiveCount = 1;
          active[name] = [{
            type: 'range',
            min: minVal,
            max: maxVal,
            isSmartPrecision: (name.includes('accuracy') || name.includes('precision') || name.includes('bmpe'))
          }];
        }
      } else {
        // Handle Pill / Button Filters
        const buttons = group.querySelectorAll('button.active');
        if (buttons.length > 0) {
          groupActiveCount = buttons.length;
          const isSmart = (name.includes('accuracy') || name.includes('precision') || name.includes('bmpe'));
          active[name] = Array.from(buttons).map((btn) => ({
            type: btn.dataset.type,
            min: parseFloat(btn.dataset.min || 0),
            max: parseFloat(btn.dataset.max || 999999),
            value: btn.dataset.value,
            isSmartPrecision: isSmart
          }));
        }
      }
      // Update active state and badge for accordion header (mobile)
      if (groupActiveCount > 0) {
        group.classList.add('has-active');
        if (badge) badge.textContent = groupActiveCount;
      } else {
        group.classList.remove('has-active');
        if (badge) badge.textContent = '';
      }
    });

    cards.forEach((card) => {
      let visible = true;

      for (const key in active) {
        const filters = active[key];
        if (!filters.length) continue;

        let match = false;
        const attrVal = card.getAttribute('data-' + key);

        for (const f of filters) {
          if (matchOne(card, key, attrVal, f)) { match = true; break; }
        }

        if (!match) {
          visible = false;
          break;
        }
      }

      card.dataset.match = visible ? '1' : '0';
    });

    renderVisible(wrapper, reset !== false);
  }

  /**
   * Paging state. The section renders up to 250 items per server page and drops a
   * hidden .cf-page-info element carrying Shopify's paginate data. From that we know
   * the true total and can fetch later pages through the Section Rendering API.
   */
  function readPageInfo(root) {
    const el = root.querySelector('.cf-page-info');
    if (!el) return null;
    const n = (k) => parseInt(el.dataset[k], 10) || 0;
    const info = {
      current: n('current') || 1,
      pages: n('pages') || 1,
      items: n('items'),
      pageSize: n('pageSize') || 250,
      pageItems: n('pageItems'),
      pageProducts: n('pageProducts')
    };
    el.remove();
    return info;
  }

  function initPaging(wrapper) {
    const grid = wrapper.querySelector('.product-grid');
    const info = grid ? readPageInfo(grid) : null;
    if (!info) { wrapper._paging = null; return; }
    wrapper._paging = {
      lastPage: info.current,
      pages: info.pages,
      items: info.items,               // all results Shopify counted (search: incl. pages/articles)
      pageSize: info.pageSize,
      nonProducts: Math.max(0, info.pageItems - info.pageProducts), // skipped non-product results seen so far
      loading: false
    };
  }

  /** True product total (search results minus the pages/articles we've skipped). */
  function trueTotal(wrapper, loadedCount) {
    const pg = wrapper._paging;
    if (!pg) return loadedCount;
    return Math.max(loadedCount, pg.items - pg.nonProducts);
  }

  /** How many items the next server page will bring (0 when everything is loaded). */
  function nextBatchSize(wrapper) {
    const pg = wrapper._paging;
    if (!pg || pg.lastPage >= pg.pages) return 0;
    return Math.max(0, Math.min(pg.pageSize, pg.items - pg.lastPage * pg.pageSize));
  }

  /**
   * Shows the first N matching cards (N = page size, grows with "Show more"),
   * hides the rest, and updates the result count / Show more / Load more / empty state.
   * reset=true goes back to the first page (used after a filter change).
   */
  function renderVisible(wrapper, reset) {
    const grid = wrapper.querySelector('.product-grid');
    if (!grid) return;

    const pageSize = parseInt(wrapper.dataset.pageSize, 10) || 24;
    if (reset || !wrapper._shown) wrapper._shown = pageSize;

    const cards = Array.from(grid.querySelectorAll('.product-card'));
    const loaded = cards.length;
    let matched = 0;

    cards.forEach((card) => {
      if (card.dataset.match === '0') {
        card.style.display = 'none';
        return;
      }
      card.style.display = matched < wrapper._shown ? '' : 'none';
      matched++;
    });

    const total = trueTotal(wrapper, loaded);
    const nextBatch = nextBatchSize(wrapper);
    const moreOnServer = nextBatch > 0;
    const showing = Math.min(matched, wrapper._shown);
    const remaining = matched - showing;
    const filtered = !!wrapper.querySelector('.filter-group.has-active');
    const plural = (n) => n.toLocaleString() + (n === 1 ? ' product' : ' products');

    const countEl = wrapper.querySelector('.cf-result-count');
    if (countEl) {
      if (!filtered) {
        countEl.textContent = showing < total ? 'Showing ' + showing.toLocaleString() + ' of ' + plural(total) : plural(total);
      } else if (moreOnServer) {
        // Filters only see what's loaded so far; say so, so the shopper knows there may be more
        countEl.textContent = matched.toLocaleString() + (matched === 1 ? ' match' : ' matches') +
          ' in the first ' + loaded.toLocaleString() + ' of ' + plural(total);
      } else if (matched === 0) {
        countEl.textContent = 'No matching products';
      } else {
        countEl.textContent = showing < matched
          ? 'Showing ' + showing.toLocaleString() + ' of ' + matched.toLocaleString() + ' matching products'
          : plural(matched) + (matched === 1 ? ' matches' : ' match') + ' your filters';
      }
    }

    const moreBtn = wrapper.querySelector('.cf-show-more');
    if (moreBtn && !(wrapper._paging && wrapper._paging.loading)) {
      if (remaining > 0) {
        moreBtn.hidden = false;
        moreBtn.dataset.mode = 'show';
        // The number is how many products this click adds
        moreBtn.textContent = 'Load more (' + Math.min(pageSize, remaining).toLocaleString() + ')';
      } else if (moreOnServer) {
        moreBtn.hidden = false;
        moreBtn.dataset.mode = 'load';
        moreBtn.textContent = 'Load more (' + nextBatch.toLocaleString() + ')';
      } else {
        moreBtn.hidden = true;
        moreBtn.dataset.mode = '';
      }
    }

    const emptyEl = wrapper.querySelector('.cf-empty:not(.cf-empty-search)');
    if (emptyEl) {
      if (!emptyEl.dataset.defaultText) emptyEl.dataset.defaultText = emptyEl.textContent;
      emptyEl.hidden = matched !== 0;
      emptyEl.textContent = moreOnServer
        ? 'No matches in the first ' + loaded.toLocaleString() + ' of ' + plural(total) + ' yet. Load more to keep looking, or remove a filter.'
        : emptyEl.dataset.defaultText;
    }
  }

  /** Fetches the next server page of this section and appends its product cards. */
  async function loadNextPage(wrapper, btn) {
    const pg = wrapper._paging;
    const grid = wrapper.querySelector('.product-grid');
    if (!pg || pg.loading || !grid || nextBatchSize(wrapper) === 0) return;

    pg.loading = true;
    btn.disabled = true;
    btn.textContent = 'Loading…';

    try {
      const url = new URL(window.location.href);
      url.searchParams.set('page', String(pg.lastPage + 1));
      url.searchParams.set('section_id', wrapper.dataset.sectionId);
      const res = await fetch(url.toString(), { credentials: 'same-origin' });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const newGrid = doc.querySelector('.product-grid');
      const info = newGrid ? readPageInfo(newGrid) : null;
      const newCards = newGrid ? Array.from(newGrid.querySelectorAll('.product-card')) : [];

      // Skip anything already on the page (results can shift between requests)
      const seen = new Set(Array.from(grid.querySelectorAll('.product-card .product-title a')).map((a) => a.getAttribute('href')));
      const before = grid.querySelectorAll('.product-card[data-match="1"]').length;
      newCards.forEach((card) => {
        const href = card.querySelector('.product-title a') && card.querySelector('.product-title a').getAttribute('href');
        if (href && seen.has(href)) return;
        card.dataset.match = '1';
        grid.appendChild(document.importNode(card, true));
      });

      if (info) {
        pg.lastPage = info.current;
        pg.pages = info.pages;
        pg.items = info.items;
        pg.nonProducts += Math.max(0, info.pageItems - info.pageProducts);
      } else {
        pg.lastPage = pg.pages; // nothing usable came back; stop offering more
      }
      pg.loading = false;
      btn.disabled = false;

      // Run the current filters over the new cards without collapsing what's already shown,
      // then reveal every new match from this batch.
      applyFilters(wrapper, false);
      const after = grid.querySelectorAll('.product-card[data-match="1"]').length;
      wrapper._shown = Math.max(wrapper._shown || 0, before) + Math.max(0, after - before);

      pruneEmptyPills(wrapper);
      const sortSel = wrapper.querySelector('.custom-sort');
      if (sortSel && sortSel.value) applySort(wrapper, sortSel.value);
      else renderVisible(wrapper, false);
    } catch (err) {
      pg.loading = false;
      btn.disabled = false;
      btn.dataset.mode = 'load';
      btn.textContent = "Couldn't load more. Try again";
    }
  }

  // Initial render: every card matches until a filter is chosen
  document.querySelectorAll('.custom-filter-wrapper').forEach((wrapper) => {
    initPaging(wrapper);
    wrapper.querySelectorAll('.product-card').forEach((card) => { card.dataset.match = '1'; });
    pruneEmptyPills(wrapper);
    renderVisible(wrapper, true);
  });

  function toggleAccordion(header) {
    const group = header.closest('.filter-group');
    if (!group) return;

    const isOpen = group.classList.toggle('is-open');
    header.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
  }

  function applySort(wrapper, type) {
    const grid = wrapper.querySelector('.product-grid');
    if (!grid) return;

    const cards = Array.from(grid.querySelectorAll(':scope > .product-card'));

    cards.sort((a, b) => {
      if (type === 'price-asc') return parseFloat(a.dataset.price) - parseFloat(b.dataset.price);
      if (type === 'price-desc') return parseFloat(b.dataset.price) - parseFloat(a.dataset.price);
      if (type === 'title-asc') return String(a.dataset.title).localeCompare(String(b.dataset.title));
      if (type === 'title-desc') return String(b.dataset.title).localeCompare(String(a.dataset.title));
      return 0;
    });

    cards.forEach((card) => grid.appendChild(card));
    renderVisible(wrapper, false);
  }

  function updateSlider(slider) {
    const minInput = slider.querySelector('.min-range');
    const maxInput = slider.querySelector('.max-range');
    const track = slider.querySelector('.slider-track');
    const minLabel = slider.querySelector('.min-label');
    const maxLabel = slider.querySelector('.max-label');
    const prefix = slider.dataset.prefix || '';
    const suffix = slider.dataset.suffix || '';

    let minVal = parseFloat(minInput.value);
    let maxVal = parseFloat(maxInput.value);

    const minPercent = ((minVal - parseFloat(minInput.min)) / (parseFloat(minInput.max) - parseFloat(minInput.min))) * 100;
    const maxPercent = ((maxVal - parseFloat(maxInput.min)) / (parseFloat(maxInput.max) - parseFloat(maxInput.min))) * 100;

    track.style.left = minPercent + "%";
    track.style.width = (maxPercent - minPercent) + "%";

    // Detect decimal precision from step attribute to correctly display small numbers (e.g. 0.06, 0.08)
    const stepStr = minInput.step || '1';
    const decimalPlaces = stepStr.includes('.') ? stepStr.split('.')[1].length : 0;

    minLabel.textContent = prefix + minVal.toFixed(decimalPlaces) + suffix;
    maxLabel.textContent = prefix + maxVal.toFixed(decimalPlaces) + suffix;
  }

  // Initialize all sliders
  document.querySelectorAll('.range-slider-wrap').forEach(updateSlider);

  document.addEventListener('input', function (e) {
    const slider = e.target.closest('.range-slider-wrap');
    if (!slider) return;

    const minInput = slider.querySelector('.min-range');
    const maxInput = slider.querySelector('.max-range');

    if (e.target === minInput) {
      if (parseFloat(minInput.value) > parseFloat(maxInput.value)) {
        minInput.value = maxInput.value;
      }
    } else if (e.target === maxInput) {
      if (parseFloat(maxInput.value) < parseFloat(minInput.value)) {
        maxInput.value = minInput.value;
      }
    }

    updateSlider(slider);
  });

  // Size lookup: re-filter shortly after typing stops (and when the in/mm unit changes)
  let lookupTimer;
  document.addEventListener('input', function (e) {
    const lk = e.target.closest('.cf-lookup');
    if (!lk) return;
    const wrapper = lk.closest('.custom-filter-wrapper');
    if (!wrapper) return;
    clearTimeout(lookupTimer);
    lookupTimer = setTimeout(function () {
      applyFilters(wrapper);
      updateClearBtn(wrapper);
    }, 250);
  });

  document.addEventListener('change', function (e) {
    const slider = e.target.closest('.range-slider-wrap');
    if (slider) {
      const wrapper = slider.closest('.custom-filter-wrapper');
      if (wrapper) { applyFilters(wrapper); updateClearBtn(wrapper); }
      return;
    }

    const select = e.target.closest('.custom-sort');
    if (select) {
      const wrapper = select.closest('.custom-filter-wrapper');
      if (wrapper) applySort(wrapper, select.value);
    }
  });

  document.addEventListener('click', function (e) {
    // 0. Show more button
    const moreBtn = e.target.closest('.cf-show-more');
    if (moreBtn) {
      e.preventDefault();
      const wrapper = moreBtn.closest('.custom-filter-wrapper');
      if (wrapper && moreBtn.dataset.mode === 'load') {
        loadNextPage(wrapper, moreBtn);
      } else if (wrapper) {
        wrapper._shown = (wrapper._shown || 0) + (parseInt(wrapper.dataset.pageSize, 10) || 24);
        renderVisible(wrapper, false);
      }
      return;
    }

    // 1. Tooltip toggle handling
    const tooltipBtn = e.target.closest('.filter-tooltip-btn');
    if (tooltipBtn) {
      e.preventDefault();
      e.stopPropagation();
      const content = tooltipBtn.nextElementSibling;
      if (content && content.classList.contains('filter-tooltip-content')) {
        const isShown = content.classList.contains('show');
        document.querySelectorAll('.filter-tooltip-content.show').forEach(el => el.classList.remove('show'));
        if (!isShown) {
          content.classList.add('show');
        }
      }
      return;
    }

    // Dismiss tooltips when clicking outside
    if (!e.target.closest('.filter-tooltip-content')) {
      document.querySelectorAll('.filter-tooltip-content.show').forEach(el => el.classList.remove('show'));
    }

    // 2. Accordion Header toggle handling
    const header = e.target.closest('.filter-group-header');
    if (header) {
      e.preventDefault();
      toggleAccordion(header);
      return;
    }

    // 3. Pill button filter selection
    const btn = e.target.closest('.filter-group button:not(.filter-tooltip-btn)');
    if (!btn) return;

    e.preventDefault();
    const wrapper = btn.closest('.custom-filter-wrapper');
    if (!wrapper) return;

    btn.classList.toggle('active');
    applyFilters(wrapper);
    updateClearBtn(wrapper);
  });

  // Keyboard accessibility for accordion headers
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Enter' || e.key === ' ') {
      const header = e.target.closest('.filter-group-header');
      if (header && !e.target.closest('.filter-tooltip-btn')) {
        e.preventDefault();
        toggleAccordion(header);
      }
    }
  });

  // ===== MOBILE: CLEAR ALL FILTERS BUTTON =====
  function updateClearBtn(wrapper) {
    const hasActive = !!wrapper.querySelector('.filter-group.has-active');
    const clearBtn = wrapper.querySelector('#mobile-clear-all-filters');
    if (clearBtn) {
      clearBtn.classList.toggle('is-visible', hasActive);
    }
  }

  document.querySelectorAll('#mobile-clear-all-filters').forEach((btn) => {
    btn.addEventListener('click', () => {
      const wrapper = btn.closest('.custom-filter-wrapper') || document.querySelector('.custom-filter-wrapper');
      if (!wrapper) return;

      // Reset all pill/button filters
      wrapper.querySelectorAll('button.active').forEach((b) => b.classList.remove('active'));

      // Reset size lookup inputs
      wrapper.querySelectorAll('.cf-lookup-input').forEach((i) => { i.value = ''; });

      // Reset range sliders to their min/max limits
      wrapper.querySelectorAll('.range-slider-wrap').forEach((sliderWrap) => {
        const minInput = sliderWrap.querySelector('.min-range');
        const maxInput = sliderWrap.querySelector('.max-range');
        if (minInput) {
          minInput.value = sliderWrap.dataset.min;
          minInput.dispatchEvent(new Event('input'));
        }
        if (maxInput) {
          maxInput.value = sliderWrap.dataset.max;
          maxInput.dispatchEvent(new Event('input'));
        }
      });

      applyFilters(wrapper);
      updateClearBtn(wrapper);
    });
  });

})();